import moment from 'moment/moment';

import { ChallengesPercentages } from '../../challenges-constants';
import { ManageChallengesMultiSelectParams } from '../../components/challenges-multi-select/challenges-multi-select.types';
import { MultiSelectOptions } from '@containers/activity-router/data-grid-container/generic-grid-filters/multi-select/multi-select.types';
import {
    isInstructionHubBlockedAssignment
} from '@containers/activity-router/routes/helpers/instruction-hub/instruction-hub-helpers';
import {
    clearMultiSelectGrid,
    updateMultiSelectGridCellsAndCounters
} from '@containers/activity-router/routes/helpers/multi-select/multi-select-helpers';
import {
    getChallengesCurrentAssignment,
    getChallengesSelectedAssignments
} from '@containers/activity-router/routes/learn/challenges/helpers/slice/assignments/selected/challenges-assignments-selected-helpers';
import { ChallengesAssignment } from '@services/gateways/combined-assign-gateway/combined-assign-gateway.types';

const {
    SCORE_0,
    SCORE_50,
    SCORE_80,
    SCORE_100
} = ChallengesPercentages;

function getAssignmentType (currentAssignment: ChallengesAssignment) {
    const {
        currentAssignmentDueDate,
        currentAssignmentStartDate
    } = currentAssignment;
    const isAssignmentPastDueDate = moment().local().isAfter(moment(currentAssignmentDueDate));
    const isFutureAssignment = moment(currentAssignmentStartDate).isAfter(moment().local());

    return {
        isAssignmentPastDueDate,
        isFutureAssignment
    };
}

export function manageChallengesMultiSelect ({
    option,
    instructionHubBlockedKeys,
    gridCells,
    gridCounters,
    studentAssignments,
    selectedAssignments,
    isCellVisible
}: ManageChallengesMultiSelectParams) {
    if (option === MultiSelectOptions.CLEAR_ALL) {
        return clearMultiSelectGrid({
            gridCells,
            gridCounters
        });
    }

    Object.keys(gridCells).forEach((studentId) => {
        Object.keys(gridCells[studentId]).forEach((topicId) => {
            Object.keys(gridCells[studentId][topicId]).forEach((activityId) => {
                const currentAssignment = getChallengesCurrentAssignment({
                    activityKey: activityId,
                    studentKey: studentId,
                    studentAssignments
                });
                const isValidResult = currentAssignment && currentAssignment.resultAccuracy !== null;
                const isValidAssignment = currentAssignment &&
                    ('currentAssignmentId' in currentAssignment);
                const isVisible = !isCellVisible || isCellVisible({
                    topicKey: topicId,
                    activityKey: activityId
                });

                function genericSuccessCallback () {
                    if (!isVisible || isInstructionHubBlockedAssignment({
                        instructionHubBlockedKeys,
                        studentKey: studentId,
                        activityKey: activityId
                    })) return;

                    updateMultiSelectGridCellsAndCounters({
                        gridCells,
                        gridCounters,
                        studentId,
                        topicId,
                        activityId
                    });
                    getChallengesSelectedAssignments({
                        currentAssignment,
                        selectedAssignments,
                        isSelected: true,
                        activityKey: activityId,
                        userProfileId: studentId
                    });
                }
                switch (option) {
                case MultiSelectOptions.ALL_CELLS: {
                    genericSuccessCallback();
                    break;
                }
                case MultiSelectOptions.ASSIGNED_NOW: {
                    if (isValidAssignment) {
                        const {
                            isFutureAssignment,
                            isAssignmentPastDueDate
                        } = getAssignmentType(currentAssignment);

                        if (!isAssignmentPastDueDate && !isFutureAssignment) genericSuccessCallback();
                    }
                    break;
                }
                case MultiSelectOptions.ASSIGNED_LATER: {
                    if (isValidAssignment) {
                        const { isFutureAssignment } = getAssignmentType(currentAssignment);

                        if (isFutureAssignment) genericSuccessCallback();
                    }
                    break;
                }
                case MultiSelectOptions.OVERDUE: {
                    if (isValidAssignment) {
                        const { isAssignmentPastDueDate } = getAssignmentType(currentAssignment);

                        if (isAssignmentPastDueDate) genericSuccessCallback();
                    }
                    break;
                }
                case MultiSelectOptions.INCORRECT: {
                    if (isValidResult && !isValidAssignment) {
                        const { resultAccuracy } = currentAssignment;

                        if (resultAccuracy !== null &&
							resultAccuracy >= SCORE_0 &&
							resultAccuracy < SCORE_50) {
                            genericSuccessCallback();
                        }
                    }
                    break;
                }
                case MultiSelectOptions.PARTIALLY_CORRECT: {
                    if (isValidResult && !isValidAssignment) {
                        const { resultAccuracy } = currentAssignment;

                        if (resultAccuracy !== null &&
                            resultAccuracy >= SCORE_50 &&
                            resultAccuracy < SCORE_80) {
                            genericSuccessCallback();
                        }
                    }
                    break;
                }
                case MultiSelectOptions.CORRECT: {
                    if (isValidResult && !isValidAssignment) {
                        const { resultAccuracy } = currentAssignment;

                        if (resultAccuracy !== null &&
							resultAccuracy >= SCORE_80 &&
							resultAccuracy <= SCORE_100) {
                            genericSuccessCallback();
                        }
                    }
                    break;
                }
                }
            });
        });
    });

    return {
        gridCells,
        gridCounters,
        selectedAssignments
    };
}
